const trimZero = (s: string) => s.replace(/\.0$/, "");

export const formatValue = (
  value: number | string | null | undefined
): string => {
  const n =
    typeof value === "number" ? value : Number(value?.replace(/,/g, "").trim());

  if (!Number.isFinite(n)) return `$${String(value)}`;

  const abs = Math.abs(n);

  if (abs >= 1_000_000_000) {
    return `$${trimZero((n / 1_000_000_000).toFixed(1))}b`;
  }

  if (abs >= 1_000_000) {
    return `$${trimZero((n / 1_000_000).toFixed(1))}m`;
  }

  if (abs >= 1_000) {
    return `$${trimZero((n / 1_000).toFixed(1))}k`;
  }

  return `$${n.toFixed(1)}`;
};

export const formatValueFloor = (value: number): string => {
  if (!Number.isFinite(value)) return "$0";

  const abs = Math.abs(value);

  if (abs >= 1_000_000_000) {
    return `$${trimZero((Math.trunc(value / 100_000_000) / 10).toFixed(1))}B`;
  }

  if (abs >= 1_000_000) {
    return `$${trimZero((Math.trunc(value / 100_000) / 10).toFixed(1))}M`;
  }

  if (abs >= 1_000) {
    return `$${trimZero((Math.trunc(value / 100) / 10).toFixed(1))}K`;
  }

  return `$${Math.trunc(value)}`;
};

interface IFormatNumberOptions {
  withDollar?: boolean;
  maximumFractionDigits?: number;
}

// Constructing Intl.NumberFormat is expensive; the dashboard formats hundreds
// of values per SSR request (PH-1139), so cache one formatter per digit count.
const formatterByFractionDigits = new Map<number, Intl.NumberFormat>();

const getFractionFormatter = (
  maximumFractionDigits: number
): Intl.NumberFormat => {
  let formatter = formatterByFractionDigits.get(maximumFractionDigits);

  if (!formatter) {
    formatter = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits,
    });
    formatterByFractionDigits.set(maximumFractionDigits, formatter);
  }

  return formatter;
};

export const formatNumber = (
  value: number | string | null | undefined,
  options: IFormatNumberOptions = {}
): string => {
  const { withDollar = true } = options;
  const prefix = withDollar ? "$" : "";

  if (value === null || value === undefined || value === "") {
    return `${prefix}0`;
  }

  const n =
    typeof value === "number"
      ? value
      : Number(String(value).replace(/,/g, "").trim());

  if (!Number.isFinite(n) || Number.isNaN(n)) {
    return `${prefix}0`;
  }

  const abs = Math.abs(n);

  const formatted = getFractionFormatter(
    options.maximumFractionDigits || 0
  ).format(abs);

  return n < 0 ? `-${prefix}${formatted}` : `${prefix}${formatted}`;
};

const integerFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});

const decimalFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

interface IFormatPercentageValueOptions {
  maximumFractionDigits?: number;
  needToAbs?: boolean;
}

export const formatPercentageValue = (
  value: number | string | null | undefined,
  options: IFormatPercentageValueOptions = {
    needToAbs: true,
  }
): string => {
  if (value === undefined) {
    return "-";
  }

  if (value === null) {
    return "N/A";
  }

  const raw =
    typeof value === "number"
      ? value
      : Number(String(value).trim().replace("%", "").replace(",", "."));

  if (!Number.isFinite(raw)) {
    return "0%";
  }

  const abs = options.needToAbs ? Math.abs(raw) : raw;

  if (options.maximumFractionDigits !== undefined) {
    return `${getFractionFormatter(options.maximumFractionDigits).format(abs)}%`;
  }

  if (abs > 1) {
    return `${integerFormatter.format(Math.round(abs))}%`;
  }

  if (Number.isInteger(abs)) {
    return `${integerFormatter.format(abs)}%`;
  }

  const rounded = Math.round((abs + Number.EPSILON) * 100) / 100;

  return `${decimalFormatter.format(rounded)}%`;
};
